import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, MessageSquare, AlertTriangle, CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SellerCheckInMonitor = () => {
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchCheckins();
  }, [filter]);

  const fetchCheckins = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const response = await axios.get(`${API}/admin/seller-checkins${params}`, {
        withCredentials: true
      });
      setCheckins(response.data.checkins || []);
    } catch (error) {
      console.error("Error fetching check-ins:", error);
      toast.error("Failed to load seller check-ins");
    } finally {
      setLoading(false);
    }
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case "email_sent":
        return <Mail className="w-5 h-5 text-blue-500" />;
      case "sms_sent":
        return <MessageSquare className="w-5 h-5 text-purple-500" />;
      case "warning_sent":
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "suspended":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStageLabel = (stage) => {
    const labels = {
      email_sent: "Email Sent",
      sms_sent: "SMS Sent",
      warning_sent: "Warning Sent",
      completed: "Responded",
      suspended: "Suspended",
      no_response: "No Response"
    };
    return labels[stage] || stage;
  };

  const getResponseTypeLabel = (responseType) => {
    const labels = {
      still_active: "Still For Sale",
      under_contract: "Under Contract",
      sold: "SOLD",
      need_help: "Needs Help"
    };
    return labels[responseType] || responseType;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const filteredCheckins = filter === "all" 
    ? checkins 
    : checkins.filter(c => c.stage === filter);

  const stats = {
    total: checkins.length,
    email_sent: checkins.filter(c => c.stage === "email_sent").length,
    sms_sent: checkins.filter(c => c.stage === "sms_sent").length,
    warning_sent: checkins.filter(c => c.stage === "warning_sent").length,
    completed: checkins.filter(c => c.stage === "completed").length,
    suspended: checkins.filter(c => c.stage === "suspended").length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Seller Check-In Monitor</h2>
        <Button onClick={fetchCheckins} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="p-4">
          <div className="text-sm text-gray-600">Total</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </Card>
        <Card className="p-4 border-blue-200">
          <div className="text-sm text-gray-600">Email Sent</div>
          <div className="text-2xl font-bold text-blue-600">{stats.email_sent}</div>
        </Card>
        <Card className="p-4 border-purple-200">
          <div className="text-sm text-gray-600">SMS Sent</div>
          <div className="text-2xl font-bold text-purple-600">{stats.sms_sent}</div>
        </Card>
        <Card className="p-4 border-yellow-200">
          <div className="text-sm text-gray-600">Warnings</div>
          <div className="text-2xl font-bold text-yellow-600">{stats.warning_sent}</div>
        </Card>
        <Card className="p-4 border-green-200">
          <div className="text-sm text-gray-600">Responded</div>
          <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
        </Card>
        <Card className="p-4 border-red-200">
          <div className="text-sm text-gray-600">Suspended</div>
          <div className="text-2xl font-bold text-red-600">{stats.suspended}</div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <Button
          size="sm"
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => setFilter("all")}
        >
          All
        </Button>
        <Button
          size="sm"
          variant={filter === "email_sent" ? "default" : "outline"}
          onClick={() => setFilter("email_sent")}
        >
          Email Sent
        </Button>
        <Button
          size="sm"
          variant={filter === "sms_sent" ? "default" : "outline"}
          onClick={() => setFilter("sms_sent")}
        >
          SMS Sent
        </Button>
        <Button
          size="sm"
          variant={filter === "warning_sent" ? "default" : "outline"}
          onClick={() => setFilter("warning_sent")}
        >
          Warnings
        </Button>
        <Button
          size="sm"
          variant={filter === "completed" ? "default" : "outline"}
          onClick={() => setFilter("completed")}
        >
          Responded
        </Button>
        <Button
          size="sm"
          variant={filter === "suspended" ? "default" : "outline"}
          onClick={() => setFilter("suspended")}
        >
          Suspended
        </Button>
      </div>

      {/* Check-ins Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Listing
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Seller
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Interval
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Stage
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Response
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCheckins.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No check-ins found
                    </td>
                  </tr>
                ) : (
                  filteredCheckins.map((checkin) => (
                    <tr key={checkin.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {checkin.listing_title}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{checkin.seller_email}</div>
                        {checkin.seller_phone && (
                          <div className="text-xs text-gray-500">{checkin.seller_phone}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {checkin.check_in_interval}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          {getStageIcon(checkin.stage)}
                          <span className="text-sm">{getStageLabel(checkin.stage)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {checkin.response_type ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            checkin.response_type === "sold" ? "bg-blue-100 text-blue-800" :
                            checkin.response_type === "still_active" ? "bg-green-100 text-green-800" :
                            checkin.response_type === "under_contract" ? "bg-yellow-100 text-yellow-800" :
                            "bg-orange-100 text-orange-800"
                          }`}>
                            {getResponseTypeLabel(checkin.response_type)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(
                          checkin.response_received_at ||
                          checkin.warning_sent_at ||
                          checkin.sms_sent_at ||
                          checkin.email_sent_at
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SellerCheckInMonitor;
